<template>
  <div class="bar-chart-container">
    <h4 v-if="title">{{ title }}</h4>
    <div class="bar-chart"></div>
  </div>
</template>

<script>
import * as d3 from 'd3';

export default {
  props: ['commuters', 'title', 'colors', 'chartWidth', 'chartHeight', 'chartMargin', 'maxBarWidth'],

  data() {
    const margin = this.chartMargin || { top: 50, right: 0, bottom: 50, left: 0 };
    return {
      margin,
      width: (this.chartWidth || 1780) - margin.left - margin.right,
      height: (this.chartHeight || 754) - margin.top - margin.bottom,
      dataLength: 0,
      duration: 700,
      min: 0,
      max: 0,
      barWidth: this.maxBarWidth || 72,
      colorScale: null,
      svg: null,
      x: null,
      y: null,
    };
  },

  watch: {
    commuters: {
      handler() {
        this.update();
      },

      deep: true,
    },
  },

  mounted() {
    const { margin, width, height } = this;
    this.x = d3.scaleBand().range([0, width]).padding(0.1);
    this.xAxis = d3.axisBottom(this.x);
    this.y = d3.scaleLinear().range([height, 0]);

    this.svg = d3
      .select(this.$el.querySelector('.bar-chart'))
      .append('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

    this.colorScale = d3.scaleLinear().domain([this.min, 0, this.max]).range(['#3237b8', '#75fcfc', '#3237b8']);

    this.addLegend();
    this.update(true);
  },

  methods: {
    getMinMax() {
      this.min = d3.min(this.commuters, d => {
        return d[1];
      });
      this.max = d3.max(this.commuters, d => {
        return d[1];
      });
    },

    updateDomains() {
      this.x.domain(this.commuters.map(d => d[0]));
      this.y.domain([this.min >= 0 ? 0 : this.min, this.max]);
    },

    addLegend() {
      const { height } = this;

      const xAxisGenerator = this.xAxis.tickSizeOuter(0);
      xAxisGenerator.tickFormat(d => (typeof d === 'string' ? d.split('|')[0] : d));

      this.svg
        .append('g')
        .attr('class', 'axis axis-x')
        .attr('transform', 'translate(0,' + height + ')')
        .attr('y', (d, idx) => {
          return idx * -24;
        })
        .call(xAxisGenerator);
    },

    update() {
      const { height, duration } = this;
      var dataLength = this.commuters.length > 0 ? this.commuters[0].length - 1 : 0;
      this.getMinMax();
      this.updateDomains();
      const bars = this.svg.selectAll('.bar').data(this.commuters, d => d[0]);
      bars
        .enter()
        .append('rect')
        .attr('class', 'bar')
        .attr('fill', 'rgba(0,0,0,.1)')
        .attr('width', Math.min(this.x.bandwidth() / dataLength, this.barWidth))
        .attr('height', 0)
        .attr('y', height)
        .attr('fill', (d, idx) => {
          return this.colors ? this.colors[idx] : this.colorScale(d[idx]);
        })
        .attr('x', d => {
          return this.x(d[0]) + (this.x.bandwidth() / dataLength / 2 - this.barWidth / 2);
        })
        .merge(bars)
        .transition()
        .duration(duration)
        .attr('height', d => {
          return height - this.y(d[1]);
        })
        .attr('y', d => {
          return this.y(d[1]);
        })
        .attr('x', d => {
          return this.x(d[0]) + (this.x.bandwidth() / dataLength / 2 - this.barWidth / 2);
        });
      bars.exit().transition().duration(duration).attr('height', 0).attr('y', height).remove();

      const labels = this.svg.selectAll('.label').data(this.commuters, d => d[0]);

      const newLabels = labels
        .enter()
        .append('text')
        .attr('class', 'label')
        .attr('opacity', 0)
        .attr('font-family', 'Helvetica')
        .attr('y', height)
        .attr('text-anchor', 'middle')
        .attr('x', d => {
          return this.x(d[0]) + this.x.bandwidth() / dataLength / 2;
        });

      newLabels
        .merge(labels)
        .transition()
        .duration(duration)
        .attr('opacity', 1)
        .attr('x', d => {
          return this.x(d[0]) + this.x.bandwidth() / dataLength / 2;
        })
        .attr('y', d => {
          return this.y(d[1]) - 10;
        })
        .tween('text', function (d) {
          const text = d3.select(this).text();
          const current = isNaN(text) ? Number(text.split('%')[0]) / 100 : text;
          var i = d3.interpolate(Number(current), d[1]);
          return t => {
            d3.select(this).text(d3.format('.0%')(i(t)));
          };
        });

      labels.exit().transition().duration(duration).attr('y', height).attr('opacity', 0).remove();

      this.svg.select('.axis.axis-x').transition().duration(duration).call(this.xAxis);

      const isLong = this.commuters.filter(e => e[0].split('|')[0].length > 10 && this.commuters.length > 5).length > 0;

      const ticks = this.svg.selectAll('.tick');
      ticks.text('');
      ticks
        .append('text')
        .text(d => (typeof d === 'string' ? d.split('|')[0] : d))
        .attr('y', (_, i) => (isLong && i % 2 ? 32 : 4));
      ticks
        .append('text')
        .text(d => `(${d && typeof d === 'string' ? d.split('|')[1] : null})`)
        .attr('y', (_, i) => (isLong && i % 2 ? 44 : 16))
        .attr('class', 'country');
    },
  },
};
</script>

<style lang="scss">
@use '@/styles/colors.scss';
.bar-chart-container {
  flex: 1;
  h4 {
    margin-left: 35px;
  }
  & + .bar-chart-container {
    margin-top: 30px;
  }
}

.bar-chart-container {
  .tick {
    &:nth-child(2n) {
      text.long {
        transform: translateY(-2px) !important;
      }
    }
  }
}

.domain,
.tick,
.tick line {
  stroke: colors.$earth-color;
}
.tick line {
  stroke: transparent;
}
.tick text {
  stroke: none;
  fill: colors.$white-color;
  font-size: 22px;
  transform: translateY(20px);
  font-family: 'Source Sans Pro', Helvetica, sans-serif;
}
.tick .country {
  font-size: 10px;
  opacity: 0.8;
}
text.label {
  stroke: none;
  fill: colors.$white-color;
  font-size: 28px;
  font-family: 'Source Sans Pro', Helvetica, sans-serif;
}

.analysis-table {
  .tick text {
    fill: #000000;
    font-size: 14px;
    transform: translateY(12px);

    &.country {
      font-size: 10px;
    }
  }
  text.label {
    font-size: 20px;
    fill: #000000;
  }
}

.page {
  .tick text {
    fill: #000000;
    font-size: 14px;
    transform: translateY(12px);

    &.country {
      font-size: 10px;
    }
  }
  text.label {
    font-size: 20px;
    fill: #000000;
  }
}
</style>
